import React, { useEffect, useState } from "react";
import {
  ArrowUpRight,
  CheckCircle2,
  BarChart2,
  X,
  Search,
} from "lucide-react";
import "./CaseStudy.css";
import Navbar from "./Navbar";
import { useLocation } from "react-router-dom";

const caseStudies = [
  {
    title: "Enhancing Deployment Efficiency with DevOps",
    client: "a mid-sized e-commerce company",
    challenge:
      "was facing significant delays in software releases due to manual deployment processes and inconsistent development environments. These inefficiencies resulted in frequent downtime, delayed feature rollouts, and higher operational costs.",
    solution: [
      "CI/CD Pipeline Automation",
      "Infrastructure as Code (IaC)",
      "Containerization",
      "Monitoring & Logging",
      "Security Enhancements",
    ],
    results: [
      "Deployment frequency increased by 70%, reducing time-to-market for new features.",
      "99.9% uptime achieved, minimizing downtime and enhancing user experience.",
      "Reduction in operational costs by 30% due to automation and optimized resource utilization.",
      "Improved collaboration between development and operations teams.",
    ],
    conclusion:
      "By leveraging modern DevOps practices, the client successfully enhanced deployment efficiency, improved system reliability, and streamlined operational workflows.",
    category: "DevOps",
    tags: ["DevOps", "CI/CD", "Automation"],
    icon: "🚀",
  },
  {
    title: "AI-ML & ERP Solutions for Business Optimization",
    client: "Single Step Associates",
    challenge:
      "Single Step Associates was struggling with inefficient business processes and a lack of data-driven decision-making capabilities. Their existing systems were siloed, making it difficult to extract meaningful insights.",
    solution: [
      "AI-Powered Predictive Analytics",
      "ERP Implementation",
      "Intelligent Automation",
      "Natural Language Processing (NLP)",
      "Data-Driven Decision Making",
    ],
    results: [
      "40% increase in operational efficiency",
      "Improved customer engagement with AI-based chatbots",
      "20% reduction in costs",
      "Enhanced data accuracy and forecasting",
    ],
    conclusion:
      "By leveraging AI-ML and ERP solutions, Single Step Associates successfully optimized its business operations, improved efficiency, and enhanced customer satisfaction.",
    category: "AI & ERP",
    tags: ["AI", "ERP", "Machine Learning"],
    icon: "🧠",
  },
  {
    title: "Frontend & Backend Design for Seamless User Experience",
    client: "Mahavir Home Decor",
    challenge:
      "Mahavir Home Decor was facing issues with an outdated website that lacked a modern user interface and a seamless backend system.",
    solution: [
      "Modern Frontend Redesign",
      "Optimized Backend Performance",
      "Seamless E-commerce Integration",
      "Payment & Security Enhancements",
      "Performance Optimization",
    ],
    results: [
      "50% faster website load times",
      "30% increase in online sales",
      "Improved backend scalability",
      "Enhanced security",
    ],
    conclusion:
      "By modernizing both the frontend and backend, Mahavir Home Decor successfully improved its online presence, enhanced customer experience, and boosted sales.",
    category: "Web Development",
    tags: ["Web Development", "UI/UX", "E-commerce"],
    icon: "🖥️",
  },
  {
    title: "Custom CRM Software for Enhanced Business Operations",
    client: "Aayurvedam (a leading Ayurvedic healthcare provider)",
    challenge:
      "Aayurvedam struggled with managing customer relationships, appointment scheduling, and sales tracking. Their existing system relied on manual processes, leading to inefficiencies, missed follow-ups, and lack of centralized customer data.",
    solution: [
      "Centralized Customer Database",
      "Automated Appointment Scheduling",
      "Sales & Inventory Tracking",
      "Personalized Marketing Automation",
      "Secure Access & Role Management",
    ],
    results: [
      "40% increase in operational efficiency",
      "25% growth in customer retention",
      "Real-time insights and analytics",
      "Reduction in appointment scheduling errors by 50%",
    ],
    conclusion:
      "By implementing a custom CRM solution, Aayurvedam transformed its business operations, leading to improved efficiency, enhanced customer experience, and increased revenue.",
    category: "CRM & ERP",
    tags: ["CRM", "Healthcare", "Automation"],
    icon: "💼",
  },
  {
    title: "CRM & ERP Web Apps with DevOps Tools",
    client: "MR Group Of Companies (a diversified business conglomerate)",
    challenge:
      "MR Group Of Companies faced operational inefficiencies due to fragmented data management, lack of process automation, and slow deployment cycles. Their existing CRM and ERP systems were outdated, leading to delays in decision-making and inconsistent workflows.",
    solution: [
      "Custom CRM & ERP Development",
      "Microservices Architecture",
      "CI/CD Pipeline Implementation",
      "Infrastructure as Code (IaC)",
      "Monitoring & Security",
    ],
    results: [
      "50% improvement in operational efficiency",
      "30% reduction in deployment time",
      "Enhanced security and compliance",
      "Scalability & future growth",
    ],
    conclusion:
      "By leveraging modern CRM & ERP web apps with DevOps tools, MR Group achieved streamlined business operations, improved decision-making, and reduced costs.",
    category: "CRM & ERP",
    tags: ["CRM", "ERP", "DevOps"],
    icon: "⚙️",
  },
  {
    title: "Automation of Email Scheduler & Web Solution for Promotional Campaigns",
    client: "DIZTAL (a digital marketing solutions provider)",
    challenge:
      "DIZTAL needed an efficient and automated system to manage email scheduling and promotional campaign distribution. Their manual process resulted in inconsistencies, missed deadlines, and inefficient audience targeting.",
    solution: [
      "Automated Email Scheduling",
      "Personalized Campaign Management",
      "User-Friendly Web Portal",
      "AI-Driven Optimization",
      "Security & Compliance",
    ],
    results: [
      "40% increase in email open rates",
      "50% time savings",
      "Improved audience targeting",
      "Scalability & reliability",
    ],
    conclusion:
      "By implementing an automated email scheduler and promotional campaign management system, DIZTAL significantly improved marketing efficiency, engagement, and conversion rates.",
    category: "Marketing Automation",
    tags: ["Marketing", "Automation", "Email"],
    icon: "📧",
  },
  {
    title: "Android & iOS App Development with Web Solutions",
    client: "TS Pvt Ltd (a technology-driven enterprise)",
    challenge:
      "TS Pvt Ltd required a seamless digital ecosystem encompassing both mobile applications (Android & iOS) and a responsive web platform. Their existing system lacked mobile accessibility and efficient backend integration.",
    solution: [
      "Android & iOS App Development",
      "Custom Web Development",
      "Secure API Integration",
      "User Authentication & Security",
      "Scalability & Cloud Deployment",
    ],
    results: [
      "60% increase in customer engagement",
      "30% faster service delivery",
      "Unified digital experience",
      "Enhanced security & compliance",
    ],
    conclusion:
      "By implementing a fully integrated Android, iOS, and web solution, TS Pvt Ltd transformed its digital presence, improving user accessibility, engagement, and operational efficiency.",
    category: "Mobile Development",
    tags: ["Mobile", "Android", "iOS", "Web Development"],
    icon: "📱",
  },
  {
    title: "AI Chatbot Development for Enhanced Customer Engagement",
    client: "Lotus Prints (a custom printing and merchandise company)",
    challenge:
      "Lotus Prints faced challenges in handling customer inquiries, order tracking, and support requests efficiently. Their manual customer service process resulted in delayed responses and reduced customer satisfaction.",
    solution: [
      "24/7 Automated Customer Support",
      "Natural Language Processing (NLP)",
      "Seamless Integration",
      "Order Tracking & Assistance",
      "Personalized Recommendations",
    ],
    results: [
      "50% reduction in response time",
      "40% decrease in manual support workload",
      "20% increase in sales conversions",
      "Scalable AI solution",
    ],
    conclusion:
      "The AI chatbot implementation transformed customer support for Lotus Prints, reducing workload and dramatically improving response times and customer satisfaction.",
    category: "AI & ERP",
    tags: ["AI", "Chatbot", "Customer Support"],
    icon: "🤖",
  },
  {
    title: "Data Solution for Efficient Resource Management",
    client: "Deccan Trees (a sustainable forestry and landscaping company)",
    challenge:
      "Deccan Trees faced difficulties in managing and analyzing vast amounts of environmental and operational data. The lack of a structured data solution led to inefficiencies in resource planning and sustainability reporting.",
    solution: [
      "Centralized Data Management System",
      "Automated Data Collection & Processing",
      "Advanced Analytics & AI-Driven Insights",
      "Interactive Dashboards & Reports",
      "Scalable & Secure Architecture",
    ],
    results: [
      "30% increase in operational efficiency",
      "25% reduction in resource wastage",
      "Real-time decision-making",
      "Enhanced accuracy",
    ],
    conclusion:
      "By implementing a robust data solution, Deccan Trees optimized resource management, improved efficiency, and enhanced sustainability initiatives.",
    category: "Data & Cloud",
    tags: ["Data", "Analytics", "Sustainability"],
    icon: "🗄️",
  },
  {
    title: "Seamless Data Migration from On-Premise to Cloud",
    client: "Theory Wise (an educational technology solutions provider)",
    challenge:
      "Theory Wise faced significant challenges with their legacy on-premise infrastructure, which resulted in high maintenance costs, limited scalability, and data accessibility issues.",
    solution: [
      "Comprehensive Assessment & Planning",
      "Cloud Infrastructure Setup",
      "Automated Data Migration",
      "Enhanced Security & Compliance",
      "Performance Optimization & Monitoring",
    ],
    results: [
      "40% reduction in operational costs",
      "Increased scalability",
      "99.9% uptime reliability",
      "Enhanced security and compliance",
    ],
    conclusion:
      "By migrating from on-premise to the cloud, Theory Wise achieved a modern, scalable, and cost-effective data infrastructure, improving operational efficiency and future-proofing their business.",
    category: "Data & Cloud",
    tags: ["Cloud", "Migration", "Infrastructure"],
    icon: "☁️",
  },
  {
    title: "Disaster Recovery and Cybersecurity Prevention",
    client: "Curios TeD (a digital learning and content platform)",
    challenge:
      "Curios TeD faced cybersecurity vulnerabilities and lacked a robust disaster recovery plan. Their existing infrastructure was susceptible to data breaches, ransomware attacks, and system failures.",
    solution: [
      "Comprehensive Security Audit",
      "Advanced Threat Protection",
      "Data Backup & Disaster Recovery",
      "Incident Response & Monitoring",
      "Compliance & Governance",
    ],
    results: [
      "99.99% uptime achieved",
      "80% reduction in cybersecurity threats",
      "Zero data loss incidents",
      "Improved compliance posture",
    ],
    conclusion:
      "With a strong cybersecurity and disaster recovery framework, Curios TeD now operates with enhanced security, reduced risks, and seamless business continuity.",
    category: "Cybersecurity",
    tags: ["Cybersecurity", "Disaster Recovery", "Compliance"],
    icon: "🛡️",
  },
  {
    title: "Automation Solution with Terraform IaC and Jenkins CI/CD",
    client: "Craffictra (a transportation and logistics solutions provider)",
    challenge:
      "Craffictra required a highly automated and scalable infrastructure to support its growing business demands. Their existing infrastructure provisioning process was manual, leading to delays and increased operational costs.",
    solution: [
      "Infrastructure as Code (IaC) with Terraform",
      "CI/CD Pipeline with Jenkins",
      "Automated Monitoring & Logging",
      "Security & Compliance Automation",
      "Scalability & High Availability",
    ],
    results: [
      "60% reduction in infrastructure deployment time",
      "40% faster release cycles",
      "Improved system reliability and uptime",
      "Enhanced security compliance",
    ],
    conclusion:
      "By leveraging Terraform IaC, Jenkins CI/CD, and a full suite of monitoring tools, Craffictra successfully automated its infrastructure and deployment processes.",
    category: "DevOps",
    tags: ["DevOps", "Terraform", "Jenkins", "CI/CD"],
    icon: "🔧",
  },
];

const caseStudyImages = [
  "https://cdn.pixabay.com/photo/2020/03/26/22/29/server-4971994_1280.png",
  "https://cdn.pixabay.com/photo/2024/03/13/19/06/ai-generated-8631634_640.jpg",
  "https://cdn.pixabay.com/photo/2016/11/23/14/45/coding-1853305_640.jpg",
  "https://cdn.pixabay.com/photo/2015/01/08/18/29/startup-593341_640.jpg",
  "https://cdn.pixabay.com/photo/2018/05/10/11/34/concept-3387334_640.jpg",
  "https://cdn.pixabay.com/photo/2017/07/31/11/21/people-2557396_640.jpg",
  "https://cdn.pixabay.com/photo/2016/11/10/16/03/android-1814556_640.jpg",
  "https://cdn.pixabay.com/photo/2018/05/08/08/44/artificial-intelligence-3382507_640.jpg",
  "https://cdn.pixabay.com/photo/2020/06/08/05/39/problem-5273058_640.jpg",
  "https://cdn.pixabay.com/photo/2019/06/14/09/25/cloud-4273197_1280.png",
  "https://cdn.pixabay.com/photo/2014/02/13/07/28/security-265130_640.jpg",
  "https://cdn.pixabay.com/photo/2024/03/13/19/06/ai-generated-8631634_640.jpg",
];

function Appp() {
  const location = useLocation();

  // Read ?study= param to open a specific study on load
  const initialIndex = (() => {
    const params = new URLSearchParams(location.search);
    const study = parseInt(params.get("study"), 10);
    if (!isNaN(study) && study >= 0 && study < caseStudies.length) return study;
    return null;
  })();

  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedStudy, setExpandedStudy] = useState(
    initialIndex !== null ? caseStudies[initialIndex] : null
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Scroll to detail panel when a study is expanded
  useEffect(() => {
    if (expandedStudy) {
      setTimeout(() => {
        document.getElementById("cs-detail-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [expandedStudy]);

  const allCategories = ["All", ...Array.from(new Set(caseStudies.map((s) => s.category)))];

  const filtered = caseStudies.filter((s) => {
    const matchCat = activeCategory === "All" || s.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q ||
      s.title.toLowerCase().includes(q) ||
      s.category.toLowerCase().includes(q) ||
      s.tags.some((t) => t.toLowerCase().includes(q));
    return matchCat && matchSearch;
  });

  const expandedIndex = expandedStudy
    ? caseStudies.findIndex((s) => s.title === expandedStudy.title)
    : -1;

  return (
    <>
      <Navbar />
      <div className="app">
        {/* Header */}
        <header className="header">
          <span className="case-eyebrow">Client Success Portfolio</span>
          <h1>Case Studies</h1>
          <p>
            Explore how IT Softlab delivers measurable business outcomes through
            software engineering, automation, cloud, AI, cybersecurity, and
            digital transformation.
          </p>
        </header>

        {/* Filter Bar */}
        <div className="cs-filter-bar">
          {/* Search */}
          <div className="cs-search-wrap">
            <Search size={16} className="cs-search-icon" />
            <input
              className="cs-search"
              type="text"
              placeholder="Search case studies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Category chips */}
          <div className="cs-chips">
            {allCategories.map((cat) => (
              <button
                key={cat}
                className={`cs-chip${activeCategory === cat ? " cs-chip--active" : ""}`}
                onClick={() => {
                  setActiveCategory(cat);
                  setExpandedStudy(null);
                }}
              >
                {cat}
                <span className="cs-chip-count">
                  {cat === "All"
                    ? caseStudies.length
                    : caseStudies.filter((s) => s.category === cat).length}
                </span>
              </button>
            ))}
          </div>

          <span className="cs-result-count">
            {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Cards Grid */}
        <div className="cs-grid">
          {filtered.length === 0 && (
            <div className="cs-empty">No case studies match your filter.</div>
          )}
          {filtered.map((study, i) => {
            // const globalIdx = caseStudies.findIndex((s) => s.title === study.title);
            const isExpanded = expandedStudy?.title === study.title;
            return (
              <div
                key={study.title}
                className={`cs-card${isExpanded ? " cs-card--active" : ""}`}
                onClick={() => setExpandedStudy(isExpanded ? null : study)}
              >
                <div className="cs-card-top">
                  <div className="cs-card-icon">{study.icon}</div>
                  <span className="cs-card-category">{study.category}</span>
                </div>
                <h3 className="cs-card-title">{study.title}</h3>
                <p className="cs-card-client">Client: {study.client}</p>
                <div className="cs-card-tags">
                  {study.tags.map((tag) => (
                    <span key={tag} className="cs-tag">{tag}</span>
                  ))}
                </div>
                <span className="cs-card-cta">
                  {isExpanded ? "Close ✕" : "View Details →"}
                </span>
              </div>
            );
          })}
        </div>

        {/* Expanded Detail Panel */}
        {expandedStudy && (
          <div className="cs-detail-panel" id="cs-detail-panel">
            <div className="cs-detail-inner">
              {/* Close */}
              <button className="cs-detail-close" onClick={() => setExpandedStudy(null)}>
                <X size={20} /> Close
              </button>

              {/* Hero image */}
              <div
                className="cs-detail-hero"
                style={{
                  backgroundImage: `linear-gradient(rgba(15,23,42,0.15), rgba(15,23,42,0.55)), url("${caseStudyImages[expandedIndex % caseStudyImages.length]}")`,
                }}
              >
                <span className="cs-detail-eyebrow">Case Study · {expandedStudy.category}</span>
                <h2>{expandedStudy.title}</h2>
                <p>Client: {expandedStudy.client}</p>
              </div>

              <div className="cs-detail-body">
                {/* Challenge */}
                <section className="cs-detail-section cs-full">
                  <h4>The Challenge</h4>
                  <p>{expandedStudy.challenge}</p>
                </section>

                {/* Solution */}
                <section className="cs-detail-section">
                  <h4>Our Solution</h4>
                  <ul className="cs-solution-list">
                    {expandedStudy.solution.map((item) => (
                      <li key={item}>
                        <CheckCircle2 size={16} className="cs-icon" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </section>

                {/* Results */}
                <section className="cs-detail-section">
                  <h4>Key Results</h4>
                  <div className="cs-results-grid">
                    {expandedStudy.results.map((result) => (
                      <div key={result} className="cs-result-card">
                        <BarChart2 size={16} className="cs-icon" />
                        <p>{result}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Conclusion */}
                <section className="cs-detail-section cs-full">
                  <h4>Conclusion</h4>
                  <p>{expandedStudy.conclusion}</p>
                </section>

                <div className="cs-full">
                  <a href="/contactus" className="contact-btn">
                    Contact Us Today <ArrowUpRight size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ height: 60 }} />
      </div>
    </>
  );
}

export default Appp;
